import React from 'react';
import { CourseMappingsPageInitialProps, ReferenceData } from '../data/useCourseMappingsPageProps';
import { schema, Parameters } from '../schema';

export type StaticProps = {
  referenceData: ReferenceData;
  defaultParameters: Parameters;
};

const staticPropsContext = React.createContext<StaticProps | undefined>(undefined);

export interface StaticPropsContextProps {
  initialProps: CourseMappingsPageInitialProps;
  children: React.ReactNode;
}

export function StaticPropsContextProvider(props: StaticPropsContextProps) {
  const value = React.useMemo(
    () => ({
      referenceData: props.initialProps.referenceData,
      defaultParameters: getDefaultQueryParameters(props.initialProps),
    }),
    [props.initialProps]
  );
  return <staticPropsContext.Provider value={value}>{props.children}</staticPropsContext.Provider>;
}

export function useStaticProps() {
  const value = React.useContext(staticPropsContext);
  if (value === undefined) {
    throw new Error('useStaticProps must be used within a StaticPropsContextProvider');
  }
  return value;
}

function getDefaultQueryParameters({ initialQueryParameters }: CourseMappingsPageInitialProps) {
  type PartialRecord<K extends keyof Parameters, T> = { [P in K]?: T };
  const defaultValues: PartialRecord<keyof Parameters, unknown> = {};
  schema.parameters.forEach((parameter) => {
    const value = parameter.getDefaultValue({
      defaultParameters: {
        pageNumber: 1,
        pageSize: initialQueryParameters.pageSize,
        archived: undefined,
        country: [],
        sort: initialQueryParameters.sort,
      },
    });
    defaultValues[parameter.key] = value;
  });
  return defaultValues as Parameters;
}
